import React, { useState, useEffect } from 'react';
import './SocioForm.css';
import { apiFetch } from '../../../services/dashboardApi';

function SocioForm({ socio, token, onCancelar, onSuccess, defaultEmail }) {
    const [formData, setFormData] = useState({
        apellidos: '',
        nombres: '',
        fecha_nac: '',
        lugar_nac: '',
        ciudad: '',
        nacionalidad: '',
        ci: '',
        complemento: '',
        expedido: '',
        telefono: '',
        celular: '',
        domicilio: '',
        email: defaultEmail || '',
        nreg_profesional: '',
        avatar: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'error' | 'success'
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    useEffect(() => {
        if (socio) {
            setFormData({
                apellidos: socio.apellidos || '',
                nombres: socio.nombres || '',
                fecha_nac: socio.fecha_nac || '',
                lugar_nac: socio.lugar_nac || '',
                ciudad: socio.ciudad || '',
                nacionalidad: socio.nacionalidad || '',
                ci: socio.ci || '',
                complemento: socio.complemento || '',
                expedido: socio.expedido || '',
                telefono: socio.telefono || '',
                celular: socio.celular || '',
                domicilio: socio.domicilio || '',
                email: socio.email || defaultEmail || '',
                nreg_profesional: socio.nreg_profesional || '',
                avatar: socio.avatar || ''
            });
            // Mostrar foto existente si hay
            if (socio.avatar) {
                const avatarUrl = socio.avatar.startsWith('http')
                    ? socio.avatar
                    : `http://localhost:8000/media/${socio.avatar}`;
                setPhotoPreview(avatarUrl);
            }
        }
        if (!socio && defaultEmail) {
            setFormData((prev) => ({ ...prev, email: defaultEmail }));
        }
    }, [socio, defaultEmail]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'email') return; // email se mantiene bloqueado
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tipo de archivo
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!validTypes.includes(file.type)) {
                setMessage('Solo se permiten imágenes JPG, JPEG o PNG');
                setMessageType('error');
                return;
            }
            // Validar tamaño (máximo 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setMessage('La imagen no debe superar los 5MB');
                setMessageType('error');
                return;
            }
            setPhotoFile(file);
            // Crear preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setMessageType('');

        try {
            const url = socio
                ? `http://localhost:8000/api/socios/${socio.id}/`
                : 'http://localhost:8000/api/socios/';

            const method = socio ? 'PATCH' : 'POST';

            // Usar siempre FormData; NO agregar 'avatar' desde formData que puede ser una URL/string
            const formDataToSend = new FormData();

            // Agregar todos los campos de texto EXCLUYENDO 'avatar'
            Object.keys(formData).forEach(key => {
                if (key === 'avatar') return; // evitar enviar string como 'avatar'
                const val = formData[key];
                if (val !== '' && val !== null && typeof val !== 'undefined') {
                    formDataToSend.append(key, val);
                }
            });

            // Asegurar que email siempre se envía
            formDataToSend.set('email', formData.email || defaultEmail || socio?.email || '');

            // Solo agregar avatar si hay un archivo nuevo
            if (photoFile) {
                formDataToSend.set('avatar', photoFile);
            }

            const headers = {
                'Authorization': `Bearer ${token}`,
                // No establecer Content-Type; dejar que el navegador lo haga
            };

            const response = await apiFetch(url, {
                method,
                headers,
                body: formDataToSend
            });

            const data = await response.json();

            if (response.ok) {
                const successMsg = socio ? 'Socio actualizado exitosamente' : 'Socio creado exitosamente';
                setMessage(successMsg);
                setMessageType('success');
                if (onSuccess) {
                    onSuccess(data.socio || data);
                } else {
                    setTimeout(() => onCancelar(), 1500);
                }
            } else {
                let errorMsg = 'Error desconocido';
                if (data.errors) {
                    errorMsg = Object.values(data.errors).flat().join(', ');
                } else if (data.detail) {
                    errorMsg = data.detail;
                } else if (data.message) {
                    errorMsg = data.message;
                }
                setMessage('Error: ' + errorMsg);
                setMessageType('error');
            }
        } catch (err) {
            setMessage('Error: ' + err.message);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h3>{socio ? 'Editar Socio' : 'Crear Nuevo Socio'}</h3>
            <form onSubmit={handleSubmit} className="form-max-width">
                <div className="form-group">
                    <label>Apellidos:*</label>
                    <input
                        type="text"
                        name="apellidos"
                        value={formData.apellidos}
                        onChange={handleChange}
                        required
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Nombres:*</label>
                    <input
                        type="text"
                        name="nombres"
                        value={formData.nombres}
                        onChange={handleChange}
                        required
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Fecha de Nacimiento:</label>
                    <input
                        type="date"
                        name="fecha_nac"
                        value={formData.fecha_nac}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Lugar de Nacimiento:</label>
                    <input
                        type="text"
                        name="lugar_nac"
                        value={formData.lugar_nac}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Ciudad:</label>
                    <input
                        type="text"
                        name="ciudad"
                        value={formData.ciudad}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Nacionalidad:</label>
                    <input
                        type="text"
                        name="nacionalidad"
                        value={formData.nacionalidad}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>CI:</label>
                    <input
                        type="number"
                        name="ci"
                        value={formData.ci}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Complemento CI:</label>
                    <input
                        type="text"
                        name="complemento"
                        value={formData.complemento}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Expedido en:</label>
                    <input
                        type="text"
                        name="expedido"
                        value={formData.expedido}
                        onChange={handleChange}
                        placeholder="Ej: LP, CB, SC"
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Teléfono:</label>
                    <input
                        type="number"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Celular:</label>
                    <input
                        type="number"
                        name="celular"
                        value={formData.celular}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Domicilio:</label>
                    <input
                        type="text"
                        name="domicilio"
                        value={formData.domicilio}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Email:</label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        readOnly
                        disabled
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>N° Registro Profesional:</label>
                    <input
                        type="number"
                        name="nreg_profesional"
                        value={formData.nreg_profesional}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Foto del Socio:</label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="form-input"
                    />
                    {photoPreview && (
                        <div className="photo-preview">
                            <img src={photoPreview} alt="Preview" className="preview-img" />
                            {photoFile && <p className="photo-info">✓ Nueva foto seleccionada</p>}
                            {!photoFile && socio && <p className="photo-info">Foto actual (no se cambiará)</p>}
                        </div>
                    )}
                </div>

                {message && (
                    <p className={`message ${messageType === 'error' ? 'error' : 'success'}`}>
                        {message}
                    </p>
                )}

                <div className="form-actions">
                    <button type="submit" disabled={loading} className="btn success">
                        {loading ? 'Guardando...' : (socio ? 'Actualizar' : 'Crear')}
                    </button>
                    <button type="button" onClick={onCancelar} className="btn cancel">
                        Cancelar
                    </button>
                </div>
            </form>
        </div>
    );
}

export default SocioForm;
